import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';

// Botão "Configurar" do VA, Auxílio Home Office e VT: regra de dias e valores
// mensais do cliente num lugar só (backend: core/views_config_beneficio.py).
//  • Dias que contam: dias úteis, mês cheio (30) / todos os dias, ou os dias da
//    semana escolhidos, com ou sem desconto de feriado — há cliente que não
//    desconta fim de semana, ou que paga sábado.
//  • Valor mensal: VA/HO = padrão por vínculo e por filial (mesmo editar/ da
//    tela); VT = valor de cada linha (tarifa ou valor fixo), salvo em lote.

const API = process.env.REACT_APP_API_BASE;
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('access')}` });
const SEMANA = ['Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb', 'Dom'];
const brl = (v) => (v == null || v === '' ? '—'
  : Number(v).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }));
const numTxt = (v) => (v == null ? '' : String(v).replace('.', ','));
// '1.234,56' e '31,43' e '31.43' viram o mesmo número (texto com ponto decimal)
const paraNumero = (t) => {
  const s = String(t).trim().replace(/R\$|\s/g, '');
  return s.includes(',') ? s.replace(/\./g, '').replace(',', '.') : s;
};
const mesNome = (iso) => {
  const [a, m] = String(iso || '').split('-').map(Number);
  if (!a || !m) return iso;
  return new Date(a, m - 1, 1).toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
};

export default function ConfigBeneficioModal({ tipo = 'va', clienteId, mes, cliente, onFechar, onSalvo }) {
  const [dados, setDados] = useState(null);
  const [erro, setErro] = useState('');
  const [msg, setMsg] = useState('');
  const [regra, setRegra] = useState({ modo: 'uteis', dias: [true, true, true, true, true, false, false], feriado: true });
  const [descFerias, setDescFerias] = useState(false);
  const [atrasado, setAtrasado] = useState(false);   // paga em atraso (mês da tela = referência)
  const [previa, setPrevia] = useState(null);
  const [salvando, setSalvando] = useState(false);
  const [vt, setVt] = useState([]);            // linhas do VT editáveis
  const [tarifa, setTarifa] = useState({ de: '', para: '' });
  const ehVt = tipo === 'vt';
  const alterado = useRef(false);
  const q = `?tipo=${tipo}&mes=${mes}`;

  const aplicar = useCallback((d) => {
    setDados(d);
    setRegra(d.regra);
    setDescFerias(Boolean(d.desconta_ferias));
    setAtrasado(Boolean(d.pagamento_atrasado));
    setPrevia(d.previa);
    setVt((d.valores_vt || []).map((l) => ({ ...l, _vu: numTxt(l.valor_unitario), _qtd: numTxt(l.quantidade), _mod: l.modalidade })));
  }, []);

  useEffect(() => {
    axios.get(`${API}/api/bpo-beneficios/${clienteId}/configuracao/${q}`, { headers: headers() })
      .then((r) => { aplicar(r.data); setErro(''); })
      .catch((e) => setErro(e.response?.data?.mensagem || 'Não foi possível carregar a configuração'));
  }, [clienteId, q, aplicar]);

  // prévia ao vivo: quantos dias a regra escolhida dá nos próximos meses
  useEffect(() => {
    if (!dados) return undefined;
    const t = setTimeout(() => {
      axios.post(`${API}/api/bpo-beneficios/${clienteId}/configuracao/${q}`,
        { acao: 'previa', tipo, mes, ...regra, pagamento_atrasado: !ehVt && atrasado }, { headers: headers() })
        .then((r) => setPrevia(r.data.previa))
        .catch((e) => setPrevia(null) || setErro(e.response?.data?.mensagem || ''));
    }, 250);
    return () => clearTimeout(t);
  }, [regra, atrasado, ehVt, dados, clienteId, q, tipo, mes]);

  const fechar = () => { onFechar(); if (alterado.current && onSalvo) onSalvo(); };

  const salvarRegra = () => {
    setSalvando(true);
    const corpo = { acao: 'salvar', tipo, mes, ...regra };
    if (!ehVt) { corpo.desconta_ferias = descFerias; corpo.pagamento_atrasado = atrasado; }
    axios.post(`${API}/api/bpo-beneficios/${clienteId}/configuracao/${q}`, corpo, { headers: headers() })
      .then((r) => { aplicar(r.data); setMsg(r.data.mensagem); setErro(''); alterado.current = true; })
      .catch((e) => setErro(e.response?.data?.mensagem || 'Não foi possível salvar'))
      .finally(() => setSalvando(false));
  };

  // VA/HO: um campo por vez, no mesmo endpoint da tela
  const salvarPadrao = (corpo, okMsg) => axios
    .patch(`${API}/api/bpo-beneficios/${clienteId}/editar/?tipo=${tipo}`, corpo, { headers: headers() })
    .then(() => axios.get(`${API}/api/bpo-beneficios/${clienteId}/configuracao/${q}`, { headers: headers() }))
    .then((r) => { aplicar(r.data); setMsg(okMsg); setErro(''); alterado.current = true; })
    .catch((e) => setErro(e.response?.data?.mensagem || 'Não foi possível salvar'));

  const campoValor = (atual, onSalvar, rotulo) => (
    <input className="form-control form-control-sm text-end" style={{ maxWidth: 130 }}
      defaultValue={numTxt(atual)} key={`${rotulo}-${atual}`} placeholder="—" inputMode="decimal"
      aria-label={rotulo}
      onBlur={(e) => {
        const v = e.target.value.trim();
        if (v === numTxt(atual)) return;
        onSalvar(v === '' ? null : paraNumero(v));
      }} />
  );

  // VT: edição em lote das linhas
  const vtAlteradas = vt.filter((l) => l._vu !== numTxt(l.valor_unitario) || l._qtd !== numTxt(l.quantidade) || l._mod !== l.modalidade);
  const mudarVt = (id, campo, valor) => setVt((ls) => ls.map((l) => (l.id === id ? { ...l, [campo]: valor } : l)));
  const trocarTarifa = () => {
    const de = tarifa.de.trim(); const para = tarifa.para.trim();
    if (!de || !para) return;
    const bate = (l) => l._mod === 'passagem' && Number(paraNumero(l._vu)) === Number(paraNumero(de));
    const n = vt.filter(bate).length;
    setVt((ls) => ls.map((l) => (bate(l) ? { ...l, _vu: para } : l)));
    setMsg(n ? `${n} linha(s) com a tarifa ${de} trocadas para ${para} — confira e clique em Salvar valores`
      : `Nenhuma linha com a tarifa ${de}`);
  };
  const salvarVt = () => {
    setSalvando(true);
    const itens = vtAlteradas.map((l) => ({ id: l.id, modalidade: l._mod, quantidade: l._qtd, valor_unitario: l._vu }));
    axios.post(`${API}/api/bpo-beneficios/${clienteId}/configuracao/${q}`, { acao: 'vt_valores', tipo, mes, itens }, { headers: headers() })
      .then((r) => { aplicar(r.data); setMsg(r.data.mensagem); setErro(''); alterado.current = true; })
      .catch((e) => setErro(e.response?.data?.mensagem || 'Não foi possível salvar'))
      .finally(() => setSalvando(false));
  };

  const regraMudou = dados && (regra.modo !== dados.regra.modo || regra.feriado !== dados.regra.feriado
    || regra.dias.some((d, i) => d !== dados.regra.dias[i])
    || (!ehVt && (descFerias !== Boolean(dados.desconta_ferias) || atrasado !== Boolean(dados.pagamento_atrasado))));

  return (
    <div className="modal show d-block" tabIndex="-1" style={{ background: 'rgba(0,0,0,.45)' }}>
      <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
        <div className="modal-content shadow-lg">
          <div className="modal-header" style={{ backgroundColor: '#ffffff', borderBottom: '1px solid #e9ecef' }}>
            <div>
              <h5 className="modal-title mb-0 fw-bold" style={{ color: '#212529' }}>
                <i className="fas fa-sliders me-2" style={{ color: '#00b5cc' }}></i>
                Configurar {dados?.tipo_rotulo || ''}
              </h5>
              <small style={{ color: '#6c757d' }}>{cliente} · a regra é do cliente e vale para qualquer mês aberto; o que já foi marcado como pago continua registrado como foi</small>
            </div>
            <button type="button" className="btn-close" onClick={fechar}></button>
          </div>

          <div className="modal-body">
            {erro && <div className="alert alert-danger py-2" role="button" onClick={() => setErro('')}><i className="fas fa-circle-exclamation me-2"></i>{erro}</div>}
            {msg && <div className="alert alert-success py-2" role="button" onClick={() => setMsg('')}><i className="fas fa-circle-check me-2"></i>{msg}</div>}
            {!dados && !erro && (
              <div className="text-center py-5 text-muted"><i className="fas fa-spinner fa-spin fa-2x mb-2" style={{ color: '#00b5cc' }}></i><div>Carregando configuração...</div></div>
            )}

            {dados && (
              <>
                {/* ── 1. DIAS QUE CONTAM ── */}
                <section className="border rounded p-3 mb-3">
                  <h6 className="fw-bold mb-1" style={{ color: '#00205b' }}><i className="fas fa-calendar-week me-2" style={{ color: '#00b5cc' }}></i>Dias que contam no mês</h6>
                  <div className="small text-muted mb-3">Regra atual: <b>{dados.rotulo}</b>. Vale para o cálculo da tela, o arquivo da plataforma, o boleto e a folha.</div>
                  <div className="row g-3">
                    <div className="col-lg-6">
                      <div className="form-check mb-2">
                        <input className="form-check-input" type="radio" id="rd-uteis" checked={regra.modo === 'uteis'}
                          onChange={() => setRegra({ modo: 'uteis', dias: [true, true, true, true, true, false, false], feriado: true })} />
                        <label className="form-check-label" htmlFor="rd-uteis"><b>Dias úteis</b> — segunda a sexta, sem feriados</label>
                      </div>
                      <div className="form-check mb-2">
                        <input className="form-check-input" type="radio" id="rd-corr" checked={regra.modo === 'corridos'}
                          onChange={() => setRegra({ modo: 'corridos', dias: [true, true, true, true, true, true, true], feriado: false })} />
                        <label className="form-check-label" htmlFor="rd-corr">
                          {ehVt ? <><b>Todos os dias do calendário</b> — não desconta fim de semana nem feriado</>
                            : <><b>Mês cheio = 30 dias</b> — não desconta fim de semana nem feriado (como Caju e VR contam)</>}
                        </label>
                      </div>
                      <div className="form-check">
                        <input className="form-check-input" type="radio" id="rd-sem" checked={regra.modo === 'semana'}
                          onChange={() => setRegra((r) => ({ modo: 'semana', dias: r.modo === 'corridos' ? [true, true, true, true, true, true, false] : r.dias, feriado: r.modo === 'corridos' ? true : r.feriado }))} />
                        <label className="form-check-label" htmlFor="rd-sem"><b>Escolher os dias da semana</b></label>
                      </div>
                      {regra.modo === 'semana' && (
                        <div className="ms-4 mt-2">
                          <div className="btn-group btn-group-sm flex-wrap mb-2" role="group" aria-label="Dias da semana que contam">
                            {SEMANA.map((d, i) => (
                              <button key={d} type="button" aria-pressed={regra.dias[i]}
                                className={`btn ${regra.dias[i] ? 'btn-primary' : 'btn-outline-secondary'}`}
                                onClick={() => setRegra((r) => ({ ...r, dias: r.dias.map((x, j) => (j === i ? !x : x)) }))}>{d}</button>
                            ))}
                          </div>
                          <div className="form-check">
                            <input className="form-check-input" type="checkbox" id="ck-fer" checked={regra.feriado}
                              onChange={(e) => setRegra((r) => ({ ...r, feriado: e.target.checked }))} />
                            <label className="form-check-label" htmlFor="ck-fer">Descontar feriados</label>
                          </div>
                        </div>
                      )}
                      {!ehVt && (
                        <div className="form-check form-switch mt-3">
                          <input className="form-check-input" type="checkbox" id="ck-ferias" checked={descFerias}
                            onChange={(e) => setDescFerias(e.target.checked)} />
                          <label className="form-check-label" htmlFor="ck-ferias">Descontar férias do crédito</label>
                        </div>
                      )}
                      {!ehVt && (
                        <div className="form-check form-switch mt-2">
                          <input className="form-check-input" type="checkbox" id="ck-atraso" checked={atrasado}
                            onChange={(e) => setAtrasado(e.target.checked)} />
                          <label className="form-check-label" htmlFor="ck-atraso">
                            <b>Paga em atraso</b> — o mês da tela é o do benefício e da folha (descontos desse mês); o colaborador recebe no mês seguinte
                            <div className="small text-muted">Ex.: o de agosto é pago em setembro e entra na folha de setembro. Desligado, é pago adiantado (pago em setembro = crédito de outubro).</div>
                          </label>
                        </div>
                      )}
                    </div>
                    <div className="col-lg-6">
                      <div className="small fw-bold mb-1">Como fica nos próximos meses</div>
                      <table className="table table-sm align-middle mb-2">
                        <thead><tr><th>Mês de uso</th><th className="text-end">Com esta regra</th><th className="text-end">Regra padrão</th></tr></thead>
                        <tbody>
                          {(previa || []).map((p) => (
                            <tr key={p.mes_uso}>
                              <td style={{ textTransform: 'capitalize' }}>{mesNome(p.mes_uso)}</td>
                              <td className="text-end fw-bold">{p.dias} dias</td>
                              <td className={`text-end ${p.dias !== p.dias_padrao ? 'text-muted' : ''}`}>{p.dias_padrao} dias</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      <div className="small text-muted">Quem entra, sai ou se afasta no meio do mês continua recebendo proporcional aos dias desta regra.</div>
                    </div>
                  </div>
                  <div className="text-end mt-2">
                    <button className="btn btn-primary btn-sm" onClick={salvarRegra} disabled={salvando || !regraMudou}>
                      <i className={`fas ${salvando ? 'fa-spinner fa-spin' : 'fa-floppy-disk'} me-2`}></i>{ehVt ? 'Salvar regra de dias' : 'Salvar regra'}
                    </button>
                  </div>
                </section>

                {/* ── 2. VALOR MENSAL ── */}
                <section className="border rounded p-3">
                  <h6 className="fw-bold mb-1" style={{ color: '#00205b' }}><i className="fas fa-sack-dollar me-2" style={{ color: '#00b5cc' }}></i>Valor mensal</h6>
                  {!ehVt ? (
                    <>
                      <div className="small text-muted mb-2">
                        Valor fixo no mês ou valor por dia. Com valor fixo, quem recebe o mês inteiro ganha o valor cheio e quem tem menos dias recebe proporcional.
                        Cada campo salva ao sair dele. Vazio volta ao padrão.
                      </div>
                      <table className="table table-sm align-middle">
                        <thead><tr><th>Vínculo</th><th className="text-end">Valor fixo no mês</th><th className="text-end">ou R$ por dia</th></tr></thead>
                        <tbody>
                          {dados.vinculos.map((p) => (
                            <tr key={p.vinculo}>
                              <td>{p.vinculo}</td>
                              <td><div className="d-flex justify-content-end">{campoValor(p.custo_mes, (v) => salvarPadrao({ tipo: 'padrao', vinculo: p.vinculo, custo_mes: v }, `${p.vinculo}: valor mensal salvo`), `Valor mensal ${p.vinculo}`)}</div></td>
                              <td><div className="d-flex justify-content-end">{campoValor(p.custo_dia, (v) => salvarPadrao({ tipo: 'padrao', vinculo: p.vinculo, custo_dia: v }, `${p.vinculo}: valor por dia salvo`), `Valor por dia ${p.vinculo}`)}</div></td>
                            </tr>
                          ))}
                          {!dados.vinculos.length && <tr><td colSpan={3} className="text-muted text-center">Nenhum vínculo encontrado nesta empresa.</td></tr>}
                        </tbody>
                      </table>
                      {dados.filiais.length > 0 && (
                        <>
                          <div className="small fw-bold mt-3 mb-1">Por filial <span className="fw-normal text-muted">(vale mais que o vínculo)</span></div>
                          <table className="table table-sm align-middle">
                            <thead><tr><th>Filial</th><th className="text-end">Valor fixo no mês</th><th className="text-end">ou R$ por dia</th></tr></thead>
                            <tbody>
                              {dados.filiais.map((f) => (
                                <tr key={f.filial_id}>
                                  <td>{f.filial}</td>
                                  <td><div className="d-flex justify-content-end">{campoValor(f.custo_mes, (v) => salvarPadrao({ tipo: 'filial', filial_id: f.filial_id, custo_mes: v }, `${f.filial}: valor mensal salvo`), `Valor mensal ${f.filial}`)}</div></td>
                                  <td><div className="d-flex justify-content-end">{campoValor(f.custo_dia, (v) => salvarPadrao({ tipo: 'filial', filial_id: f.filial_id, custo_dia: v }, `${f.filial}: valor por dia salvo`), `Valor por dia ${f.filial}`)}</div></td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </>
                      )}
                      <div className="small text-muted">O valor individual de um colaborador continua na tabela da tela e vale mais que estes.</div>
                    </>
                  ) : (
                    <>
                      <div className="small text-muted mb-2">
                        Valor de cada linha de VT. "Por passagem" multiplica passagens × tarifa × dias; "Fixo no mês" é o valor fechado.
                        O mensal estimado usa {previa?.[0]?.dias ?? '—'} dias de {mesNome(previa?.[0]?.mes_uso)} (ou os dias fixos da pessoa).
                      </div>
                      <div className="d-flex align-items-center gap-2 flex-wrap mb-2 p-2 rounded" style={{ background: '#f6f8fb' }}>
                        <span className="small fw-bold">Reajuste de tarifa:</span>
                        <span className="small">trocar</span>
                        <input className="form-control form-control-sm" style={{ width: 90 }} placeholder="5,75" value={tarifa.de} onChange={(e) => setTarifa((t) => ({ ...t, de: e.target.value }))} aria-label="Tarifa atual" />
                        <span className="small">por</span>
                        <input className="form-control form-control-sm" style={{ width: 90 }} placeholder="6,00" value={tarifa.para} onChange={(e) => setTarifa((t) => ({ ...t, para: e.target.value }))} aria-label="Tarifa nova" />
                        <button type="button" className="btn btn-sm btn-outline-primary" onClick={trocarTarifa} disabled={!tarifa.de || !tarifa.para}>Aplicar nas linhas</button>
                      </div>
                      <div style={{ maxHeight: 360, overflowY: 'auto' }}>
                        <table className="table table-sm align-middle">
                          <thead className="table-light" style={{ position: 'sticky', top: 0 }}>
                            <tr><th>Colaborador</th><th>Unidade</th><th>Linha</th><th>Modalidade</th><th className="text-end">Passagens/dia</th><th className="text-end">Tarifa ou valor fixo</th><th className="text-end">Mensal estimado</th></tr>
                          </thead>
                          <tbody>
                            {vt.map((l) => {
                              const mudou = vtAlteradas.includes(l);
                              return (
                                <tr key={l.id} style={mudou ? { background: '#fff5da' } : undefined}>
                                  <td className="small">{l.nome}</td>
                                  <td className="small">{l.unidade}</td>
                                  <td className="small">{l.linha || '—'}</td>
                                  <td>
                                    <select className="form-select form-select-sm" value={l._mod} onChange={(e) => mudarVt(l.id, '_mod', e.target.value)} aria-label="Modalidade">
                                      <option value="passagem">Por passagem</option>
                                      <option value="fixo">Fixo no mês</option>
                                    </select>
                                  </td>
                                  <td><div className="d-flex justify-content-end">
                                    <input className="form-control form-control-sm text-end" style={{ width: 70 }} value={l._mod === 'fixo' ? '—' : l._qtd} disabled={l._mod === 'fixo'}
                                      onChange={(e) => mudarVt(l.id, '_qtd', e.target.value)} aria-label="Passagens por dia" />
                                  </div></td>
                                  <td><div className="d-flex justify-content-end">
                                    <input className="form-control form-control-sm text-end" style={{ width: 110 }} value={l._vu} inputMode="decimal"
                                      onChange={(e) => mudarVt(l.id, '_vu', e.target.value)} aria-label="Tarifa ou valor fixo" />
                                  </div></td>
                                  <td className="text-end small">{mudou ? <span className="text-muted">salve para recalcular</span> : brl(l.mensal_estimado)}</td>
                                </tr>
                              );
                            })}
                            {!vt.length && <tr><td colSpan={7} className="text-muted text-center">Nenhuma linha de VT cadastrada. Inclua os colaboradores na tela do VT.</td></tr>}
                          </tbody>
                        </table>
                      </div>
                      <div className="text-end">
                        <button className="btn btn-primary btn-sm" onClick={salvarVt} disabled={salvando || !vtAlteradas.length}>
                          <i className={`fas ${salvando ? 'fa-spinner fa-spin' : 'fa-floppy-disk'} me-2`}></i>Salvar valores{vtAlteradas.length ? ` (${vtAlteradas.length})` : ''}
                        </button>
                      </div>
                    </>
                  )}
                </section>
              </>
            )}
          </div>
          <div className="modal-footer bg-light">
            <button className="btn btn-secondary" onClick={fechar}><i className="fas fa-times me-2"></i>Fechar</button>
          </div>
        </div>
      </div>
    </div>
  );
}
